import { useState, useEffect, useCallback, useRef } from 'react';
import { videosAPI, subscriptionAPI } from '../services/api';
import SubscriptionModal from '../components/SubscriptionModal';
import ChannelPlayer from '../components/ChannelPlayer';
import {
  HiSearch, HiRefresh, HiPlay, HiStatusOnline,
} from 'react-icons/hi';

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [playerChannel, setPlayerChannel] = useState(null);
  const pendingChannelRef = useRef(null);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await videosAPI.getAll();
      if (data.success) setChannels(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    try {
      const { data } = await subscriptionAPI.getMySubscription();
      if (data.success || data.subscribed !== undefined) {
        setIsSubscribed(data.subscribed || data.data?.subscribed || false);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);
  useEffect(() => { checkSubscription(); }, [checkSubscription]);

  // Dynamic categories from the channel list (like kitongatv)
  const categories = ['All', ...Array.from(new Set(channels.map((c) => c.category).filter(Boolean)))];

  const filtered = channels.filter((ch) => {
    const matchSearch =
      !search ||
      ch.name?.toLowerCase().includes(search.toLowerCase()) ||
      ch.description?.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || ch.category === activeCategory;
    return matchSearch && matchCat;
  });

  const handleChannelClick = (channel) => {
    if (isSubscribed) {
      setPlayerChannel(channel);
    } else {
      pendingChannelRef.current = channel;
      setShowSubModal(true);
    }
  };

  const handleSubSuccess = () => {
    setIsSubscribed(true);
    setShowSubModal(false);
    if (pendingChannelRef.current) {
      setPlayerChannel(pendingChannelRef.current);
      pendingChannelRef.current = null;
    }
  };

  return (
    <div className="channels-page">
      <div className="ch-top">
        <h1><HiStatusOnline size={24} /> Live TV</h1>
        <button className="ch-refresh-btn" onClick={fetchChannels} title="Refresh">
          <HiRefresh size={18} />
        </button>
      </div>

      {/* Subscription banner */}
      {!isSubscribed && (
        <div className="ch-sub-banner" onClick={() => setShowSubModal(true)}>
          <div className="ch-sub-banner-text">
            <strong>Subscribe to unlock all channels</strong>
            <span>Get access to premium live TV channels</span>
          </div>
          <button className="ch-sub-banner-btn">Subscribe</button>
        </div>
      )}

      {/* Search */}
      <div className="ch-search">
        <HiSearch className="ch-search-icon" />
        <input
          type="text"
          placeholder="Search channels..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Categories */}
      {categories.length > 1 && (
        <div className="ch-categories">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`ch-cat-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Channels Grid */}
      {loading ? (
        <div className="ch-loading">
          <div className="loader-spinner" />
          <p>Loading channels...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ch-empty">
          <HiStatusOnline size={48} />
          <p>No channels available</p>
        </div>
      ) : (
        <div className="ch-grid">
          {filtered.map((channel) => (
            <div
              key={channel._id}
              className="ch-card"
              onClick={() => handleChannelClick(channel)}
            >
              <div className="ch-card-thumb">
                {channel.thumbnail ? (
                  <img src={channel.thumbnail} alt={channel.name} loading="lazy" />
                ) : (
                  <div className="ch-card-placeholder">
                    <HiPlay size={32} />
                  </div>
                )}
                <div className="ch-card-overlay">
                  <div className="ch-card-play"><HiPlay size={24} /></div>
                </div>
                <div className="ch-card-live">
                  <span className="ch-live-dot" />
                  LIVE
                </div>
                {channel.requiresPayment && (
                  <div className="ch-card-premium">PREMIUM</div>
                )}
                {!isSubscribed && (
                  <div className="ch-card-lock">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="ch-card-body">
                <h3>{channel.name}</h3>
                <div className="ch-card-meta">
                  {channel.category && (
                    <span className="ch-card-cat">{channel.category}</span>
                  )}
                  {(channel.linkType === 'DRM' || channel.linkType === 'DASH') && (
                    <span className="ch-card-type">{channel.linkType}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Subscription Modal */}
      {showSubModal && (
        <SubscriptionModal
          onClose={() => { setShowSubModal(false); pendingChannelRef.current = null; }}
          onSuccess={handleSubSuccess}
        />
      )}

      {/* Player (YouTube-style watch page) */}
      {playerChannel && (
        <ChannelPlayer
          channel={playerChannel}
          channels={channels}
          onClose={() => setPlayerChannel(null)}
          onSelectChannel={(c) => setPlayerChannel(c)}
        />
      )}
    </div>
  );
}
